/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Row,
  Spin,
  Typography,
  message,
  notification,
  Modal,
  Progress,
  Select,
  Input,
} from 'antd';
import {
  CheckCircleTwoTone,
  CheckOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  FileSearchOutlined,
  SendOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { API_HOST } from 'utils/env';
import Table from 'components/Table';
import { formatReceivedValues } from 'utils/formatValues';
import { getEmpleadosWithoutFilter } from 'api/estructuraOrganizacional/empleados';
import {
  getRecibosDeNomina,
  iniciarTimbrado,
  iniciarConsulta,
  iniciarCancelacionNomina,
  cancelarRecibo,
  interpretarSolicitudCancelacion,
  consultarEstadoCancelacion,
  describirConsultaEstado,
  esEjercicioAnterior,
  liberarBloqueoTimbrado,
  getProgresoProceso,
  MAX_CONSULTAS_SEGUIMIENTO,
  hayConsultaPendiente,
  BANDERA_CANCELACION,
  BANDERA_CONSULTA_ESTADO,
  getOperacionesHabilitadas,
  validarRecibosParaTimbrado,
  guardarDatosRecibo,
} from 'api/nominas/timbrado';
import { seguirProceso } from 'api/nominas/pollingProceso';
import { DESENLACE } from 'api/nominas/seguimientoProceso';
import ModalConfirmation from 'components/ModalConfirmation';
import DatosTimbradoRecibo from 'components/Nominas/Timbrado/DatosTimbradoRecibo';
import DialogoCancelacionTimbrado from 'components/Nominas/Timbrado/DialogoCancelacionTimbrado';
import ValidacionReciboTimbrado from 'components/Nominas/Timbrado/ValidacionReciboTimbrado';
import CorreccionDatosRecibo from 'components/Nominas/Timbrado/CorreccionDatosRecibo';
import { getPlazasOptimized } from 'api/catalogos/plazas';
import currencyFormatter from 'utils/currencyFormatter';
import { checkToken } from 'api/utils';
import {
  ESTATUS_TIMBRADO_SIN_TIMBRAR,
  ESTATUS_TIMBRADO_CON_ERROR,
  ESTATUS_TIMBRADO_TIMBRADO,
  ESTATUS_TIMBRADO_CANCELADO,
  ESTATUS_TIMBRADO_PENDIENTE_CONCILIAR,
} from 'utils/constants';
import EstatusTimbradoTag from 'components/Nominas/Timbrado/EstatusTimbradoTag';
import { puedeTimbrarTodo } from 'utils/estatusTimbrado';

// Con todos sus recibos pendientes de conciliar no hay nada que timbrar, y no se deben reenviar
const MENSAJE_NOMINA_PENDIENTE_CONCILIAR = 'Los recibos pendientes de esta nómina están por conciliar con el '
  + 'proveedor; no se deben volver a timbrar.';

const { Text } = Typography;

// C4: estatus que el servidor puede timbrar y por tanto validar (los demás regresan `no_aplica`)
const ESTATUS_VALIDABLES = [ESTATUS_TIMBRADO_SIN_TIMBRAR, ESTATUS_TIMBRADO_CON_ERROR];
export const MENSAJE_SOLO_UN_RECIBO = 'Seleccione exactamente un recibo para esta acción.';
export const MENSAJE_REVISION_CON_PROCESO = 'Hay un proceso de timbrado en curso: espere a que termine '
  + 'para corregir el recibo.';

/**
 * Si el recibo se puede seleccionar: no, cuando la validación previa del servidor determinó que no
 * es timbrable. Los recibos sin validación o que no se validan (p. ej. Timbrados, para cancelar)
 * siguen seleccionables; el servidor vuelve a validar al timbrar.
 */
export const reciboSeleccionable = (recibo, porRecibo) => {
  if (!ESTATUS_VALIDABLES.includes(recibo?.tipo_de_estatus_timbrado)) return true;
  const validacion = porRecibo?.[recibo.id];
  return !(validacion && validacion.estado !== 'no_aplica' && validacion.timbrable === false);
};

// Mínimo que exige el servidor para el motivo de la liberación (ticket 18)
export const MOTIVO_LIBERACION_MIN = 10;

// D4 (ticket 22): Timbrar Todo y Cancelar Todo exigen un permiso propio del timbrado, el
// mismo que valida el servidor (nominas/utils/permisos_timbrado.py en SAC).
export const PERMISO_TIMBRAR_TODO = { model: 'nominaplazatimbrado', action: 'autorizar' };
export const PERMISO_CANCELAR_TODO = { model: 'nominaplazatimbrado', action: 'cancelar' };
// Ticket 37 (TC-1): cancelar un recibo exige el mismo permiso que Cancelar Todo, el que
// valida el servidor (PuedeCancelarNomina).
export const PERMISO_CANCELAR_TIMBRADO = PERMISO_CANCELAR_TODO;
// Ticket 50 (H-1): Consultar Estado tiene permiso propio, distinto del de cancelar, el que
// valida el servidor (PuedeConsultarEstadoCancelacion): add_ del modelo que registra cada consulta.
export const PERMISO_CONSULTAR_ESTADO = {
  model: 'consultaestadocancelacionsysticloud', action: 'add',
};

// Ticket 53: motivo por el que no se ofrece una acción gobernada por una bandera de SAC
export const MENSAJE_VERIFICANDO_BANDERA = 'Verificando si la operación está habilitada en el '
  + 'servidor.';
export const MENSAJE_BANDERA_NO_VERIFICADA = 'No se pudo verificar si la operación está habilitada '
  + 'en el servidor. Vuelva a abrir el detalle para reintentar.';
export const MENSAJES_BANDERA_APAGADA = {
  [BANDERA_CANCELACION]: `La cancelación SystiCloud no está habilitada (${BANDERA_CANCELACION}).`,
  [BANDERA_CONSULTA_ESTADO]:
    `La consulta de estado SystiCloud no está habilitada (${BANDERA_CONSULTA_ESTADO}).`,
};

export const timbradoNominaDetallePermission = {
  permissionModel: 'nominaplaza',
  catalogsModels: [],
  tabsModels: [],
};

const TimbradoNominaDetalle = ({
  permission,
  selected,
  encabezado,
  fetchEncabezado,
  onClickEdit,
  fetchData,
  estatusTimbrado,
}) => {
  const [data, setData] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // C4: la selección se conserva al paginar; guarda los registros para las acciones de un recibo
  const [selectedRows, setSelectedRows] = useState([]);
  // C4: validación previa de la página visible, { porRecibo, cargando, error }
  const [validaciones, setValidaciones] = useState({
    porRecibo: {}, cargando: false, error: false,
  });
  // Descarta respuestas de validación de una página o nómina anterior
  const validacionRef = useRef(0);
  const [empleados, setEmpleados] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const [pageActual, setPageActual] = useState(1);
  // Ticket 3: búsqueda de la tabla y filtro por estatus de timbrado. En refs para que la
  // recarga al terminar un proceso (callback del seguimiento) los conserve.
  const [estatusFiltro, setEstatusFiltro] = useState(null);
  const consultaRef = useRef(null);
  const estatusRef = useRef(null);
  // Seguimiento del proceso de timbrado en background
  // procesoActivo: { proceso, titulo, mensajeExito } | null
  const [procesoActivo, setProcesoActivo] = useState(null);
  const [progresoProceso, setProgresoProceso] = useState({ en_curso: 0, total: 0 });
  // Si ya se vio el proceso en curso (habilita o no "Dejar de Esperar")
  const [inicioConfirmado, setInicioConfirmado] = useState(false);
  // Acción pendiente de confirmación: 'consultar' | 'timbrar' | null
  const [accionPorConfirmar, setAccionPorConfirmar] = useState(null);
  // Cancelación pendiente de elegir motivo (ticket 37): 'recibo' | 'todo' | null
  const [cancelacionPorConfirmar, setCancelacionPorConfirmar] = useState(null);
  // Liberación manual del bloqueo de Timbrar Todo (ticket 18): motivo capturado, o null si cerrado
  const [motivoLiberacion, setMotivoLiberacion] = useState(null);
  // Recibo cuyo panel de Datos de Timbrado está abierto
  const [reciboDatosTimbrado, setReciboDatosTimbrado] = useState(null);
  // Recibo Sin Timbrar/Con Error abierto para revisar y corregir sus datos
  const [reciboEnRevision, setReciboEnRevision] = useState(null);
  const [validandoRecibo, setValidandoRecibo] = useState(false);
  // Ticket 53: banderas que el servidor reportó apagadas (409), { [bandera]: detalle }. Respaldo
  // si una bandera se apaga después de abrir el detalle.
  const [banderasApagadas, setBanderasApagadas] = useState({});
  // Ticket 53: banderas leídas al abrir el detalle; null mientras se consultan
  const [operacionesHabilitadas, setOperacionesHabilitadas] = useState(null);

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
        nombre_empleado: e?.empleado,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const limpiarSeleccion = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  // C4: valida en el servidor (solo lectura) los recibos 1/2 de la página, sin pulsar nada
  const validarPagina = async (results) => {
    validacionRef.current += 1;
    const turno = validacionRef.current;
    const ids = (results || [])
      .filter((recibo) => ESTATUS_VALIDABLES.includes(recibo.tipo_de_estatus_timbrado))
      .map((recibo) => recibo.id);
    if (!ids.length) {
      setValidaciones({ porRecibo: {}, cargando: false, error: false });
      return;
    }
    setValidaciones({ porRecibo: {}, cargando: true, error: false });
    const { error, porRecibo } = await validarRecibosParaTimbrado(selected, ids);
    if (turno === validacionRef.current) setValidaciones({ porRecibo, cargando: false, error });
  };

  // La tabla llama con (página, búsqueda); sin búsqueda (recarga) se reutiliza la última
  const fetchPaginated = async (page = 1, q) => {
    if (q !== undefined) consultaRef.current = q;
    setLoading(true);
    const cleanPage = Number(String(page).replace(/\D/g, '')) || 1; // solo dígitos
    const response = await getRecibosDeNomina(
      selected,
      cleanPage,
      consultaRef.current,
      estatusRef.current,
    );
    if (response) {
      const results = formatReceivedClone(response.results);
      setPageActual(cleanPage);
      setData({ ...response, results });
      validarPagina(results);
    }
    setLoading(false);
  };

  const onChangeEstatusFiltro = (estatus) => {
    estatusRef.current = estatus ?? null;
    setEstatusFiltro(estatus ?? null);
    limpiarSeleccion();
    fetchPaginated(1);
  };

  useEffect(() => {
    let mounted = true;
    // Otra nómina: el filtro por estatus no se arrastra
    estatusRef.current = null;
    setEstatusFiltro(null);
    setPageActual(1);
    limpiarSeleccion();
    setReciboEnRevision(null);
    const fetchAll = async () => {
      await checkToken();
      setLoading(true);
      const [
        // _empleados,
        // _plazas,
        _data,
      ] = await Promise.all([
        // getEmpleadosWithoutFilter(),
        // getPlazasOptimized(),
        // La búsqueda capturada en la tabla se conserva al cambiar de nómina
        getRecibosDeNomina(selected, 1, consultaRef.current),
      ]);
      if (mounted) {
        // setEmpleados(_empleados);
        // setPlazas(_plazas);
        if (_data) {
          const results = formatReceivedClone(_data.results);
          setData({ ..._data, results });
          validarPagina(results);
        }
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [selected]);

  // C4: casillas con el patrón de Nombramientos (Checkbox en columnTitle, sin onSelectAll). La
  // tabla se pagina en el servidor: "todos" son los recibos de la página visible, y la selección
  // de otras páginas se conserva.
  const paginaVisible = data?.results || [];
  const seleccionablesEnPagina = paginaVisible.filter(
    (row) => reciboSeleccionable(row, validaciones.porRecibo),
  );
  const seleccionadosEnPagina = seleccionablesEnPagina.filter(
    (row) => selectedRowKeys.includes(row.id),
  );
  const seleccionar = (rows, seleccionado) => {
    const ids = rows.map((row) => row.id);
    if (seleccionado) {
      setSelectedRowKeys((prev) => [...prev, ...ids.filter((id) => !prev.includes(id))]);
      setSelectedRows((prev) => [
        ...prev,
        ...rows.filter((row) => !prev.some((p) => p.id === row.id)),
      ]);
    } else {
      setSelectedRowKeys((prev) => prev.filter((id) => !ids.includes(id)));
      setSelectedRows((prev) => prev.filter((row) => !ids.includes(row.id)));
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        aria-label="Seleccionar los recibos de la página"
        checked={seleccionablesEnPagina.length > 0
          && seleccionadosEnPagina.length === seleccionablesEnPagina.length}
        indeterminate={seleccionadosEnPagina.length > 0
          && seleccionadosEnPagina.length < seleccionablesEnPagina.length}
        disabled={!seleccionablesEnPagina.length}
        onChange={(e) => seleccionar(seleccionablesEnPagina, e.target.checked)}
      />
    ),
    onSelect: (record, seleccionado) => seleccionar([record], seleccionado),
    // La validación previa determinó que no es timbrable: se corrige primero desde la revisión
    getCheckboxProps: (record) => ({
      disabled: !reciboSeleccionable(record, validaciones.porRecibo),
    }),
  };

  // Los recibos Sin Timbrar/Con Error se abren para revisar y corregir; los demás conservan el
  // clic para seleccionar (Cancelar, Consultar Estado y Datos de Timbrado son de un recibo).
  const handleOnRowClick = (record) => {
    if (ESTATUS_VALIDABLES.includes(record.tipo_de_estatus_timbrado)) {
      setReciboEnRevision(record);
      return;
    }
    seleccionar([record], !selectedRowKeys.includes(record.id));
    form.setFieldsValue(record);
  };

  // Vuelve a validar en el servidor (solo lectura) el recibo en revisión
  const revalidarRecibo = async (recibo = reciboEnRevision) => {
    if (!recibo) return;
    const turno = validacionRef.current;
    setValidandoRecibo(true);
    const { error, porRecibo } = await validarRecibosParaTimbrado(selected, [recibo.id]);
    setValidandoRecibo(false);
    if (turno !== validacionRef.current) return;
    if (error) {
      message.warning('No se pudo validar el recibo. Intente de nuevo.');
      return;
    }
    setValidaciones((prev) => ({ ...prev, porRecibo: { ...prev.porRecibo, ...porRecibo } }));
    if (porRecibo[recibo.id] && !reciboSeleccionable(recibo, porRecibo)) {
      seleccionar([recibo], false);
    }
  };

  // Guarda los datos corregidos (el servidor decide si el recibo se puede modificar) y revalida
  const guardarCorreccion = async (cambios) => {
    const actualizado = await guardarDatosRecibo(reciboEnRevision, cambios);
    if (!actualizado) return;
    const recibo = { ...reciboEnRevision, ...formatReceivedValues(actualizado) };
    setData((prev) => ({
      ...prev,
      results: (prev?.results || []).map(
        (row) => (row.id === recibo.id ? { ...row, ...recibo } : row),
      ),
    }));
    setReciboEnRevision(recibo);
    message.success('Datos del recibo guardados.');
    await revalidarRecibo(recibo);
  };

  // Aviso al usuario según cómo terminó el seguimiento del proceso
  const finalizarSeguimiento = ({ titulo, mensajeExito }, desenlace, errores = []) => {
    setProcesoActivo(null);
    setProgresoProceso({ en_curso: 0, total: 0 });
    setInicioConfirmado(false);

    const avisos = {
      [DESENLACE.ERRORES_CONSULTA]: ['warning', 'No se pudo consultar el progreso; el proceso puede seguir '
        + 'en cola o en ejecución. Consulte más tarde antes de volver a lanzarlo.'],
      [DESENLACE.ERRORES_PROCESO]: ['warning', errores.join(' | ')],
      [DESENLACE.TERMINADO]: ['success', mensajeExito],
      // Ticket 30: terminó, pero la fila global no prueba que su resultado sea de esta corrida
      [DESENLACE.TERMINADO_SIN_RESULTADO]: ['info', 'El proceso terminó, pero no se pudo '
        + 'confirmar su resultado. Revise el estatus de los recibos.'],
      [DESENLACE.INICIO_NO_CONFIRMADO]: ['warning', 'No se pudo confirmar el inicio del proceso; '
        + 'puede seguir en cola. Consulte más tarde antes de volver a lanzarlo.'],
      [DESENLACE.ESPERA_ABANDONADA]: ['warning', 'Se dejó de esperar sin confirmar el inicio del '
        + 'proceso: puede seguir en cola o haber terminado. Revise el estatus de la nómina antes '
        + 'de volver a lanzarlo.'],
      [DESENLACE.SIGUE_EN_EJECUCION]: ['warning', 'El proceso sigue en ejecución. Consulte '
        + 'nuevamente más tarde.'],
    };
    const [tipo, description] = avisos[desenlace] || avisos[DESENLACE.SIGUE_EN_EJECUCION];
    notification[tipo]({
      message: titulo,
      description,
      placement: 'topRight',
      duration: 0,
    });

    // Refrescar encabezado, listado de nóminas y plazas de la nómina (los estatus cambiaron)
    limpiarSeleccion();
    fetchEncabezado(selected);
    fetchData();
    fetchPaginated(pageActual);
  };

  // Ticket 53: lee las banderas de SAC al abrir el detalle
  useEffect(() => {
    let mounted = true;
    getOperacionesHabilitadas().then((resultado) => {
      if (mounted) setOperacionesHabilitadas(resultado || { error: true });
    });
    return () => { mounted = false; };
  }, []);

  // Motivo para no ofrecer una acción que gobierna `bandera` (`clave` en la respuesta), o null.
  // Mientras se consulta, con un error o con la bandera apagada no se ofrece: no sale ninguna
  // petición.
  const motivoBanderaApagada = (bandera, clave) => {
    if (banderasApagadas[bandera]) return banderasApagadas[bandera];
    if (!operacionesHabilitadas) return MENSAJE_VERIFICANDO_BANDERA;
    if (operacionesHabilitadas.error) return MENSAJE_BANDERA_NO_VERIFICADA;
    return operacionesHabilitadas[clave] === true ? null : MENSAJES_BANDERA_APAGADA[bandera];
  };
  const motivoCancelacion = motivoBanderaApagada(BANDERA_CANCELACION, 'cancelacion');
  const motivoConsultaEstado = motivoBanderaApagada(BANDERA_CONSULTA_ESTADO, 'consultaEstado');

  // Polling del progreso del proceso de timbrado en curso
  useEffect(() => {
    if (!procesoActivo) return () => {};
    setInicioConfirmado(false);

    // Al desmontar o al cambiar procesoActivo se detiene; respuestas tardías se ignoran
    return seguirProceso({
      consultar: (opciones) => getProgresoProceso(procesoActivo.proceso, opciones),
      maxConsultas: MAX_CONSULTAS_SEGUIMIENTO,
      onAvance: ({ enCurso, total }) => setProgresoProceso({ en_curso: enCurso, total }),
      onInicioConfirmado: () => setInicioConfirmado(true),
      onFin: (desenlace, progreso) => finalizarSeguimiento(
        procesoActivo,
        desenlace,
        progreso.errores,
      ),
    });
    // eslint-disable-next-line
  }, [procesoActivo]);

  // Mientras no se confirme el inicio, el usuario puede dejar de esperar (p. ej. si
  // el proceso terminó entre dos consultas y la fila quedó igual a la previa).
  // Nunca se anuncia "terminado": solo se detiene y se refrescan los datos.
  const onDejarDeEsperar = () => {
    if (procesoActivo && !inicioConfirmado) {
      finalizarSeguimiento(procesoActivo, DESENLACE.ESPERA_ABANDONADA);
    }
  };

  const onClickConsultar = () => {
    if (!hayConsultaPendiente(encabezado)) {
      message.info('No hay una consulta de timbrado pendiente para esta nómina.');
      return;
    }
    setAccionPorConfirmar('consultar');
  };

  const onClickTimbrar = () => {
    if (encabezado?.tipo_de_estatus_timbrado === ESTATUS_TIMBRADO_TIMBRADO) {
      message.info('La nómina ya fue timbrada correctamente en todos sus registros.');
      return;
    }
    if (encabezado?.tipo_de_estatus_timbrado === ESTATUS_TIMBRADO_PENDIENTE_CONCILIAR) {
      message.info(MENSAJE_NOMINA_PENDIENTE_CONCILIAR);
      return;
    }
    if (!encabezado.nomina_termino_servicio_timbrado) {
      setAccionPorConfirmar('timbrar');
    }
  };

  const onClickCancelarTodo = () => {
    if (motivoCancelacion) return;
    setCancelacionPorConfirmar('todo');
  };

  const onClickLiberarBloqueo = () => setMotivoLiberacion('');

  const confirmarLiberacion = async () => {
    setLoading(true);
    try {
      const { liberado, mensaje } = await liberarBloqueoTimbrado(
        encabezado?.id,
        motivoLiberacion.trim(),
      );
      if (liberado) {
        setMotivoLiberacion(null);
        message.success(mensaje || 'Se liberó el bloqueo de timbrado de la nómina.');
        await fetchEncabezado?.(selected);
      }
    } finally {
      setLoading(false);
    }
  };

  // Acciones de un solo recibo: solo con exactamente uno seleccionado (C4)
  const reciboSeleccionado = () => (selectedRows.length === 1 ? selectedRows[0] : undefined);
  const motivoSoloUnRecibo = selectedRowKeys.length === 1 ? null : MENSAJE_SOLO_UN_RECIBO;

  const onClickTimbrarSeleccionados = () => {
    if (!selectedRowKeys.length) return;
    setAccionPorConfirmar('timbrar_seleccionados');
  };

  // Refresca encabezado, listado de nóminas y recibos tras una acción de cancelación
  const refrescarTrasCancelacion = () => {
    fetchEncabezado?.(selected);
    fetchData?.();
    fetchPaginated(pageActual);
  };

  const onClickCancelarPlaza = () => {
    if (motivoCancelacion) return;
    const recibo = reciboSeleccionado();
    if (!recibo) {
      message.info('Seleccione un recibo de la tabla para cancelar.');
      return;
    }
    if (recibo.tipo_de_estatus_timbrado !== ESTATUS_TIMBRADO_TIMBRADO) {
      message.info('Solo se puede cancelar un recibo Timbrado.');
      return;
    }
    setCancelacionPorConfirmar('recibo');
  };

  const registrarBanderaApagada = ({ banderaDeshabilitada, mensaje }) => {
    setBanderasApagadas((previas) => ({ ...previas, [banderaDeshabilitada]: mensaje }));
    notification.warning({
      message: 'Operación Deshabilitada en el Servidor',
      description: mensaje,
      placement: 'topRight',
    });
  };

  const onClickConsultarEstado = async () => {
    if (motivoConsultaEstado) return;
    const recibo = reciboSeleccionado();
    if (!recibo) {
      message.info('Seleccione un recibo de la tabla para consultar el estado de su cancelación.');
      return;
    }
    if (![ESTATUS_TIMBRADO_TIMBRADO, ESTATUS_TIMBRADO_CANCELADO]
      .includes(recibo.tipo_de_estatus_timbrado)) {
      message.info('Solo se puede consultar el estado de un recibo Timbrado o Cancelado.');
      return;
    }
    setLoading(true);
    try {
      const resultado = await consultarEstadoCancelacion(recibo.id);
      if (resultado?.banderaDeshabilitada) {
        registrarBanderaApagada(resultado);
      } else if (resultado) {
        const { tipo, renglones, aviso } = describirConsultaEstado(
          resultado,
          ESTATUS_TIMBRADO_CANCELADO,
        );
        notification[tipo]({
          message: 'Estado de la Cancelación ante el SAT',
          description: (
            <>
              {renglones.map((renglon) => <div key={renglon}>{renglon}</div>)}
              {aviso && <Text type="secondary">{aviso}</Text>}
            </>
          ),
          placement: 'topRight',
          duration: 0,
        });
        refrescarTrasCancelacion();
      }
    } finally {
      setLoading(false);
    }
  };

  const ejecutarCancelacion = async (motivo) => {
    const tipoCancelacion = cancelacionPorConfirmar;
    const numeroNomina = encabezado?.numero_nomina || '';
    setCancelacionPorConfirmar(null);
    setLoading(true);
    try {
      if (tipoCancelacion === 'recibo') {
        const resultado = await cancelarRecibo(selectedRowKeys[0], motivo);
        if (resultado?.banderaDeshabilitada) {
          registrarBanderaApagada(resultado);
        } else if (resultado) {
          const { tipo, titulo, descripcion } = interpretarSolicitudCancelacion(resultado);
          notification[tipo]({
            message: titulo, description: descripcion, placement: 'topRight', duration: 0,
          });
          refrescarTrasCancelacion();
        }
      } else if (tipoCancelacion === 'todo') {
        const inicio = await iniciarCancelacionNomina(encabezado?.id, motivo);
        const titulo = 'Cancelación de Timbrado';
        if (inicio.banderaDeshabilitada) {
          registrarBanderaApagada(inicio);
        } else if (inicio.iniciado) {
          // Ticket 31: la corrida envía solicitudes; solo la consulta de estado confirma
          setProcesoActivo({
            proceso: inicio.proceso,
            titulo,
            mensajeExito: `Se enviaron las solicitudes de cancelación de la nómina ${numeroNomina}. `
              + 'Quedan pendientes de confirmar con «Consultar Estado».',
          });
        } else if (inicio.procesoEnCurso) {
          notification.warning({ message: titulo, description: inicio.mensaje, placement: 'topRight' });
        } else if (inicio.mensaje) {
          message.info(inicio.mensaje);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const tituloCancelacion = () => {
    if (cancelacionPorConfirmar === 'todo') {
      return `Cancelar Todos los Recibos Timbrados de la Nómina ${encabezado?.numero_nomina || ''}`;
    }
    const recibo = reciboSeleccionado();
    return `Cancelar el Recibo Timbrado del Empleado ${recibo?.empleado_numero_y_nombre_completo || recibo?.numero_y_nombre_completo || ''}`;
  };

  const onClickDatosTimbrado = () => {
    const recibo = reciboSeleccionado();
    if (!recibo) {
      message.info('Seleccione un recibo de la tabla para consultar sus datos de timbrado.');
      return;
    }
    setReciboDatosTimbrado(recibo);
  };

  const tituloConfirmacion = () => {
    const numeroNomina = encabezado?.numero_nomina || '';
    switch (accionPorConfirmar) {
      case 'consultar':
        return `Consultar el estatus de timbrado de la Nómina ${numeroNomina}`;
      case 'timbrar':
        return `Timbrar todos los recibos de la Nómina ${numeroNomina}`;
      case 'timbrar_seleccionados':
        return `Timbrar ${selectedRowKeys.length} recibo(s) seleccionado(s) de la Nómina ${numeroNomina}. `
          + 'Los que no estén Sin Timbrar o Con Error, o no pasen la validación, no se envían.';
      default:
        return '';
    }
  };

  const ejecutarAccionConfirmada = async () => {
    const accion = accionPorConfirmar;
    const numeroNomina = encabezado?.numero_nomina || '';
    setAccionPorConfirmar(null);
    setLoading(true);
    try {
      // Si se lanzó un proceso en background se le da seguimiento; si no, se
      // muestra el mensaje informativo del backend (si lo hay).
      const iniciaSeguimiento = (inicio, titulo, mensajeExito) => {
        if (inicio.iniciado) {
          setProcesoActivo({ proceso: inicio.proceso, titulo, mensajeExito });
        } else if (inicio.procesoEnCurso) {
          // No se lanzó: ya había un proceso del mismo tipo en curso
          notification.warning({
            message: titulo,
            description: inicio.mensaje,
            placement: 'topRight',
          });
        } else if (inicio.mensaje) {
          message.info(inicio.mensaje);
        }
      };

      if (accion === 'consultar') {
        iniciaSeguimiento(
          await iniciarConsulta(encabezado?.id),
          'Consulta de Timbrado',
          `La consulta de timbrado de la nómina ${numeroNomina} ha finalizado.`,
        );
      } else if (accion === 'timbrar') {
        iniciaSeguimiento(
          await iniciarTimbrado(encabezado?.id),
          'Timbrado de Nómina',
          `La nómina ${numeroNomina} ha finalizado el timbrado.`,
        );
      } else if (accion === 'timbrar_seleccionados') {
        const inicio = await iniciarTimbrado(encabezado?.id, selectedRowKeys);
        if (inicio.rechazado) {
          // El servidor no lanzó nada: se conserva la selección y la pantalla sigue igual
          notification.warning({
            message: 'Timbrado de Recibos Seleccionados', description: inicio.mensaje, placement: 'topRight',
          });
          return;
        }
        if (inicio.iniciado && inicio.seleccion?.descartados) {
          message.info(`Se enviarán ${inicio.seleccion.seleccionados} recibo(s); `
            + `${inicio.seleccion.descartados} no estaban disponibles para timbrar y se omitieron.`);
        }
        iniciaSeguimiento(
          inicio,
          'Timbrado de Recibos Seleccionados',
          `Terminó el timbrado de los recibos seleccionados de la nómina ${numeroNomina}.`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'No. de Plaza',
      dataIndex: 'plaza_no',
      key: 'plaza_no',
      width: 200,
    },
    {
      titleText: 'No. de Empleado',
      dataIndex: 'empleado_numero',
      key: 'empleado_numero',
      width: 200,
    },
    {
      titleText: 'Empleado',
      dataIndex: 'empleado_numero_y_nombre_completo',
      key: 'empleado_numero_y_nombre_completo',
      width: 300,
    },
    {
      titleText: 'Importe Neto',
      dataIndex: 'importe_neto',
      key: 'importe_neto',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Estatus de Timbrado',
      dataIndex: 'tipo_de_estatus_timbrado',
      key: 'tipo_de_estatus_timbrado',
      width: 200,
      render: (val) => <EstatusTimbradoTag id={val} catalogo={estatusTimbrado} />,
    },
    {
      titleText: 'Validación',
      dataIndex: 'id',
      key: 'validacion_previa',
      width: 160,
      render: (id, record) => (
        ESTATUS_VALIDABLES.includes(record.tipo_de_estatus_timbrado) ? (
          <ValidacionReciboTimbrado
            validacion={validaciones.porRecibo[id] || null}
            cargando={validaciones.cargando}
            error={validaciones.error}
          />
        ) : <Text type="secondary">—</Text>
      ),
    },
    {
      titleText: 'Mensaje de Timbrado',
      dataIndex: 'mensaje_estatus_timbrado',
      key: 'mensaje_estatus_timbrado',
      width: 500,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Row style={{ marginBottom: 8 }}>
          <Select
            allowClear
            placeholder="Filtrar por Estatus de Timbrado"
            style={{ minWidth: 280 }}
            value={estatusFiltro ?? undefined}
            onChange={onChangeEstatusFiltro}
            options={(estatusTimbrado || []).map((estatus) => ({
              value: estatus.id,
              label: estatus.descripcion,
            }))}
          />
        </Row>
        <Table
          cols={columns}
          data={data?.results || []}
          count={data?.count || 0}
          currentPage={pageActual}
          rowSelection={rowSelection}
          pagination={false}
          paginationCallback={fetchPaginated}
          handleOnRowClick={handleOnRowClick}
          customActions={[
            {
              onClick: () => onClickConsultar(),
              text: 'Consultar',
              icon: <ExclamationCircleOutlined />,
            },
            {
              // D4 (ticket 22): el servidor exige el mismo permiso; ocultarla aquí solo es UX
              permission: PERMISO_TIMBRAR_TODO,
              onClick: () => onClickTimbrar(),
              text: 'Timbrar Todo',
              icon: <CheckOutlined />,
              // Se bloquea cuando todos los registros ya fueron timbrados correctamente, o cuando
              // lo único pendiente son recibos por conciliar (nunca se reenvían)
              disabled: !puedeTimbrarTodo(encabezado?.tipo_de_estatus_timbrado),
              tooltip: encabezado?.tipo_de_estatus_timbrado === ESTATUS_TIMBRADO_PENDIENTE_CONCILIAR
                ? MENSAJE_NOMINA_PENDIENTE_CONCILIAR
                : undefined,
            },
            {
              // C4: mismo permiso, endpoint y bloqueo que Timbrar Todo; el servidor descarta los
              // recibos no timbrables y revalida cada XML antes de enviarlo
              permission: PERMISO_TIMBRAR_TODO,
              onClick: () => onClickTimbrarSeleccionados(),
              text: 'Timbrar Seleccionados',
              icon: <SendOutlined />,
              disabled: !selectedRowKeys.length,
              tooltip: selectedRowKeys.length
                ? `${selectedRowKeys.length} recibo(s) seleccionado(s)`
                : 'Seleccione los recibos a timbrar',
            },
            {
              // Ticket 37: el permiso que exige el servidor (antes, cancelar_nominaplaza)
              permission: PERMISO_CANCELAR_TIMBRADO,
              onClick: () => onClickCancelarPlaza(),
              text: 'Cancelar',
              icon: <CloseOutlined />,
              // Ticket 53: sin la bandera de cancelación encendida en el servidor
              disabled: !!motivoCancelacion || !!motivoSoloUnRecibo,
              tooltip: motivoCancelacion || motivoSoloUnRecibo || undefined,
            },
            {
              // Ticket 50: permiso y bandera propios, independientes de la cancelación
              permission: PERMISO_CONSULTAR_ESTADO,
              onClick: () => onClickConsultarEstado(),
              text: 'Consultar Estado',
              icon: <FileSearchOutlined />,
              disabled: !!motivoConsultaEstado || !!motivoSoloUnRecibo,
              tooltip: motivoConsultaEstado || motivoSoloUnRecibo
                || 'Consulta ante el SAT si la cancelación del recibo quedó confirmada',
            },
            {
              permission: PERMISO_CANCELAR_TODO,
              onClick: () => onClickCancelarTodo(),
              text: 'Cancelar Todo',
              icon: <CloseOutlined />,
              disabled: !!motivoCancelacion,
              tooltip: motivoCancelacion || undefined,
            },
            {
              // Mismo permiso que Timbrar Todo; el servidor solo libera bloqueos sin
              // actividad reciente
              permission: PERMISO_TIMBRAR_TODO,
              onClick: () => onClickLiberarBloqueo(),
              text: 'Liberar Bloqueo',
              icon: <UnlockOutlined />,
              tooltip: 'Libera una nómina cuyo timbrado quedó atascado (sin actividad reciente)',
            },
            {
              permission: { model: 'nominaplazatimbrado', action: 'view' },
              onClick: () => onClickDatosTimbrado(),
              text: 'Datos de Timbrado',
              icon: <FileSearchOutlined />,
              disabled: !!motivoSoloUnRecibo,
              tooltip: motivoSoloUnRecibo || undefined,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <div style={{
          display: 'flex', alignItems: 'center', position: 'absolute', bottom: 0, right: 0,
        }}
        >
          <CheckCircleTwoTone
            twoToneColor="#d88f4f"
            style={{
              fontSize: '23px',
              marginRight: '5px',
            }}
          />
          <Text style={{ fontSize: '0.8rem', color: '#d88f4f' }}>
            {encabezado?.tiempo_de_timbrado}
          </Text>
        </div>
      </Spin>
      <ModalConfirmation
        onFinish={ejecutarAccionConfirmada}
        Confirm
        onCancel={() => setAccionPorConfirmar(null)}
        open={!!accionPorConfirmar}
        contentTitle={tituloConfirmacion()}
        loading={loading}
      />
      <DialogoCancelacionTimbrado
        open={!!cancelacionPorConfirmar}
        titulo={tituloCancelacion()}
        ejercicioAnterior={esEjercicioAnterior(encabezado?.fecha_final_nomina)}
        loading={loading}
        onConfirmar={ejecutarCancelacion}
        onCancelar={() => setCancelacionPorConfirmar(null)}
      />
      <Modal
        open={motivoLiberacion !== null}
        title={`Liberar el Bloqueo de Timbrado de la Nómina ${encabezado?.numero_nomina || ''}`}
        okText="Liberar"
        cancelText="Cancelar"
        onOk={confirmarLiberacion}
        onCancel={() => setMotivoLiberacion(null)}
        okButtonProps={{ disabled: (motivoLiberacion || '').trim().length < MOTIVO_LIBERACION_MIN }}
        confirmLoading={loading}
        destroyOnClose
      >
        <p>
          Use esta opción solo si el timbrado quedó atascado. Los recibos que estaban en envío
          quedan Pendientes de Conciliar y no se reenvían.
        </p>
        <Form layout="vertical">
          <Form.Item label="Motivo de la Liberación" required>
            <Input.TextArea
              aria-label="Motivo de la Liberación"
              rows={3}
              maxLength={500}
              value={motivoLiberacion || ''}
              onChange={(e) => setMotivoLiberacion(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
      <CorreccionDatosRecibo
        visible={!!reciboEnRevision}
        recibo={reciboEnRevision}
        validacion={reciboEnRevision ? validaciones.porRecibo[reciboEnRevision.id] || null : null}
        validando={validandoRecibo || (validaciones.cargando && !!reciboEnRevision)}
        soloLectura={!!procesoActivo}
        motivoSoloLectura={procesoActivo ? MENSAJE_REVISION_CON_PROCESO : null}
        seleccionado={!!reciboEnRevision && selectedRowKeys.includes(reciboEnRevision.id)}
        onGuardar={guardarCorreccion}
        onRevalidar={() => revalidarRecibo()}
        onSeleccionar={() => seleccionar([reciboEnRevision], true)}
        onVerDatosTimbrado={() => setReciboDatosTimbrado(reciboEnRevision)}
        onClose={() => setReciboEnRevision(null)}
      />
      <DatosTimbradoRecibo
        visible={!!reciboDatosTimbrado}
        onClose={() => setReciboDatosTimbrado(null)}
        recibo={reciboDatosTimbrado}
        estatusTimbrado={estatusTimbrado}
      />
      <Modal
        open={!!procesoActivo}
        title={procesoActivo?.titulo || 'Procesando...'}
        footer={inicioConfirmado ? null : (
          <Button onClick={onDejarDeEsperar}>Dejar de Esperar</Button>
        )}
        closable={false}
        maskClosable={false}
        centered
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {progresoProceso.total > 0 ? (
            <Progress
              type="circle"
              percent={Math.round((progresoProceso.en_curso / progresoProceso.total) * 100)}
            />
          ) : (
            <Spin size="large" />
          )}
          <p style={{ marginTop: 16 }}>
            {progresoProceso.total > 0
              ? `${progresoProceso.en_curso} / ${progresoProceso.total} registros procesados`
              : 'Procesando, por favor espere...'}
          </p>
          {!inicioConfirmado && (
            <Text type="secondary">
              Esperando a que el proceso inicie. Si terminó muy rápido puede no detectarse;
              en ese caso deje de esperar y revise el estatus de la nómina.
            </Text>
          )}
        </div>
      </Modal>
    </Row>
  );
};
export default TimbradoNominaDetalle;
